import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { Alert, Snackbar } from '@mui/material';
import { nanoid } from 'nanoid';

import { useFileUpload } from '@modules/_shared/utils/files/file-upload';
import { useLocale } from '@shared/hooks/locale';

export type EntityFileUploadData = {
  name: string;
  url: string;
  size: number;
  mimetype: string;
};

export const useEntityFileUpload = <TData extends any>({
  createFile,
  throwOnError = false,
}: {
  createFile: (
    file: EntityFileUploadData,
    onCompleted: (data: TData) => void,
    onError: (error: ErrorLike) => void,
  ) => void;
  throwOnError?: boolean;
}) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const addPendingFile = (file: File) =>
    setPendingFiles(prev => [...prev, file]);
  const removePendingFile = (file: File) =>
    setPendingFiles(prev => prev.filter(item => item !== file));
  const [errorFiles, setErrorFiles] = useState<
    { fileName: string; key: string }[]
  >([]);

  const addErrorFile = (fileName: string) =>
    setErrorFiles(prev => [...prev, { fileName, key: nanoid() }]);

  const removeErrorFile = (key: string) =>
    setErrorFiles(prev => prev.filter(item => item.key !== key));

  const [uploadFile] = useFileUpload('file');

  const upload = (file: File) => {
    addPendingFile(file);
    return new Promise<TData | null>((resolve, reject) =>
      uploadFile(file, ({ data, error }) => {
        if (error != null || data?.url == null) {
          addErrorFile(file.name);
          removePendingFile(file);

          return throwOnError ? reject(error) : resolve(null);
        }

        createFile(
          {
            name: file.name,
            url: data.url,
            size: file.size,
            mimetype: file.type,
          },
          data => {
            removePendingFile(file);
            resolve(data);
          },
          apolloError => {
            addErrorFile(file.name);
            removePendingFile(file);

            throwOnError ? reject(apolloError) : resolve(null);
          },
        );
      }),
    );
  };
  const FileErrors = () => {
    const { t } = useLocale();
    return (
      <>
        {errorFiles.map(({ fileName, key }) => (
          <Snackbar
            key={key}
            open={true}
            autoHideDuration={3000}
            onClose={() => removeErrorFile(key)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => removeErrorFile(key)}
              severity="error"
              sx={{ width: '100%' }}
            >
              {t('An error occured while uploading {{file}}', {
                file: fileName,
              })}
            </Alert>
          </Snackbar>
        ))}
      </>
    );
  };
  return {
    pendingFiles,
    errorFiles,
    upload,
    removeErrorFile,
    FileErrors,
  };
};
